import { Component, effect, inject, signal } from "@angular/core";
import { ActivatedRoute, RouterOutlet } from "@angular/router";
import { Spinner } from "./components/spinner/spinner";
import { Analytics } from "./services/analytics";
import { AppState } from "./services/app-state";
import { CanvasState } from "./services/canvas-state";

@Component({
    selector: "app-root",
    imports: [RouterOutlet, Spinner],
    templateUrl: "./app.html",
    styleUrl: "./app.scss",
})
export class App {
    private appState = inject(AppState);
    private route = inject(ActivatedRoute);
    private analytics = inject(Analytics);
    private canvasState = inject(CanvasState);

    protected readonly title = signal("canvas-viewer");
    readonly authorName = this.appState.authorName;
    readonly isEmbedded = this.appState.isEmbedded;
    readonly isLoading = this.appState.isLoading;

    constructor() {
        this.analytics.initAnalytics();

        // Runs initially and again whenever the manifest signal changes.
        // onCleanup removes the previous system-theme listener before rerunning
        // or when this component is destroyed, so it cannot override a fixed theme.
        effect((onCleanup) => {
            const theme = this.canvasState.manifest()?.visualTheme ?? "auto";

            if (theme === "light" || theme === "dark") {
                setTheme(theme);
                return;
            }

            // when the theme is "auto", check the system's color preference
            const mediaQuery = window.matchMedia(
                "(prefers-color-scheme: dark)"
            );

            setTheme(mediaQuery.matches ? "dark" : "light");

            // Listen for system changes and update theme reactively
            const onChange = (event: MediaQueryListEvent) => {
                setTheme(event.matches ? "dark" : "light");
            };

            mediaQuery.addEventListener("change", onChange);

            onCleanup(() => {
                mediaQuery.removeEventListener("change", onChange);
            });
        });
    }

    onFooterLinkClick(label: string) {
        const canvasId = this.getCanvasId();
        const params: Record<string, string> = {
            location: "footer",
            link_label: label,
        };
        if (canvasId) {
            params["canvas_id"] = canvasId;
        }

        this.analytics.trackEvent("canvas_footer_link_click", params);
    }

    private getCanvasId(): string | null {
        return this.route.snapshot.firstChild?.paramMap.get("canvasId") ?? null;
    }
}

function setTheme(theme: "light" | "dark") {
    document.documentElement.setAttribute("data-theme", theme);
}
